import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { EopAssistantComponent } from '../../components/eop-assistant/eop-assistant.component';
import { SpinnerComponent } from '../../components/spinner/spinner.component';
import { PopupMessageComponent } from '../../components/popup-message/popup-message.component';
import { PrvNextButtonsComponent } from '../../components/prv-next-buttons/prv-next-buttons.component';
import { IntroductionComponent } from '../../components/introduction/introduction.component';
import { UserDataService } from '../../../shared/services/userData.service';
import { Icons } from '../../../shared/helpers/icons';
import { LucideAngularModule } from 'lucide-angular';
import { FunctionService } from '../../../shared/services/function.service';
import { ThreatService } from '../../../shared/services/threat.service';
import { CardsListSkeletonComponent } from '../../components/cards-list-skeleton/cards-list-skeleton.component';
import { IThreat } from '../../../shared/models/threat';
import { IFunctionGoals } from '../../../shared/models/functionGoal';
import { FormsModule } from '@angular/forms';
import { FunctionDetailsComponent } from '../../components/function-details/function-details.component';
import { get } from 'http';
import { EopNavigationService } from '../../../shared/services/eopNavigation.service';
import { ESectionTypes } from '../../../shared/models/eop_types.enum';
import { IUserInfo } from '../../../shared/models/contact';

@Component({
  selector: 'app-functional-assignments',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    LucideAngularModule,
    EopAssistantComponent,
    IntroductionComponent,
    SpinnerComponent,
    PopupMessageComponent,
    CardsListSkeletonComponent,
    PrvNextButtonsComponent,
    FunctionDetailsComponent
  ],
  templateUrl: './functional-assignments.component.html',
  styleUrl: './functional-assignments.component.css'
})
export class FunctionalAssignmentsComponent implements OnInit{

  sectionTypeId = ESectionTypes["Functional Assignments"];

  loading: boolean = false;
  loadingFunction: boolean = true;
  showFunctionDetails: boolean = false;
  
  icons = Icons;
  currentBuildingId: string | null = null;
  currentUser: IUserInfo | null = null;
  messageModal: string | null = null;
  popupMessage: string = '';
  functionsList: any = [];
  threatsList: IThreat[] = [];
  initialSelectedFunctionsList: any[] = [];
  selectedFunctionsList: any = [];

  allAssignments: any[] = []; // All from backend
  filteredAssignments: any[] = []; // Based on threat dropdown
  latestUpdate: any = {}; // Latest update for the building

  selectedThreatId: number  = 0;
  selectedFunction: IFunctionGoals | null = null;
  // threatFunctionsList: any = [];

  constructor(
    private userDataService: UserDataService,
    private functionService: FunctionService,
    private threatService: ThreatService,
    public eopNavigationService: EopNavigationService
  ) {}

  ngOnInit(): void {
    this.currentBuildingId = this.userDataService.getBuildingId();
    this.currentUser = this.userDataService.getUserData();
    this.loadFunctions(); // load functions on init, then inside this function we will load threats, then inside threat list we load the assignments.
  }
  
  // load all functions for the left table
  loadFunctions(): void {
    if (!this.currentBuildingId) {
      console.error('Current building ID is not set.');
      return;
    }
    this.loading = true;
    this.functionService.getFunctions(this.currentBuildingId).subscribe({
      next: (response: any) => {
        this.functionsList = response.data ? response.data : [];
        // Sort the list after retrieving functions list.
        if (this.functionsList.length > 0) {
          this.functionsList.sort((a: any, b: any) => a.function_name.localeCompare(b.function_name));
        }
        this.loading = false;
        this.loadingFunction = false;
        console.log('List of functions: ', response);
        this.loadThreats(); // HERE, after loading functions, we load threats.
      },
      error: (error: any) => {
        this.loading = false;
        this.loadingFunction = false;
        console.error('Error loading Functions:', error.message);
      }
    })
  }

  // load all threats for the right table
  loadThreats(): void {
    if (!this.currentBuildingId) {
      console.error('Current building ID is not set.');
      return;
    }
    this.loading = true;
    this.threatService.getThreats(this.currentBuildingId).subscribe({
      next: (response) => {
        this.threatsList = response.data ? response.data : [];
        // Sort the list after retrieving threats list.
        if (this.threatsList.length > 0) {
          this.threatsList.sort((a: IThreat, b: IThreat) => a.threat.localeCompare(b.threat));
          // assign initial value for the threat dropdown
          if (this.selectedThreatId === 0) {
            this.selectedThreatId = this.threatsList[0].id;
          }
        }
        this.loading = false;
        this.loadingFunction = false;
        console.log('Successfully loading Threats:', response)
        this.loadAllAssignments(); // HERE, after loading threats, we load all assignments.
      },
      error: (error: any) => {
        this.loading = false;
        this.loadingFunction = false;
        console.error('Error loading Threats:', error.message);
      }
    })
  }

  // load all assignments of a building, then later will match them with the threat selected in the dropdown.
  loadAllAssignments(): void {
    if (!this.currentBuildingId) return;
    this.functionService.getAllFunctionalAssignments(this.currentBuildingId).subscribe({
      next: (response) => {
        this.allAssignments = response.data || [];
        this.getLatestUpdate(this.allAssignments);
        this.filterAssignmentsByThreat(); // filter for default threat
        console.log('Successfully loading all functional assignments:', response);
      },
      error: (error) => {
        console.error('Error loading assignments:', error.message);
      }
    });
  }

  // get latest update for the building
  getLatestUpdate(objectList: any[]): any[] {
    if (objectList.length > 0) {
      this.latestUpdate = objectList.reduce((latest, current) => {
        return new Date(current.last_updated_at) > new Date(latest.last_updated_at) ? current : latest;
      });
    }
    console.log('Latest Update:', this.latestUpdate);
    return [this.latestUpdate];
  }

  isLatestUpdateAvailable(): boolean {
    return this.latestUpdate && Object.keys(this.latestUpdate).length > 0;
  }
  
  // 2-way binding on threat dropdown.
  onThreatChange(threatId: number): void {
    this.selectedThreatId = threatId;
    this.filterAssignmentsByThreat();
  }

  filterAssignmentsByThreat(): void {
    const threatId = Number(this.selectedThreatId);
    this.selectedFunctionsList = this.allAssignments
      .filter(item => Number(item.threat_hazard_id) === threatId);
    this.initialSelectedFunctionsList = JSON.parse(JSON.stringify(this.selectedFunctionsList));
    console.log('Filtered Assignments for Threat ID:', threatId, this.selectedFunctionsList);
  }
  
  // add function to the selected threat to be saved later
  addFunctionToThreat(functionItem: IFunctionGoals): void {
    if (this.selectedFunctionsList.some((selected: any) => selected.function_name === functionItem.function_name)) {
      alert('This function has been assigned to this threat/hazard already.');
    } else {
      this.selectedFunctionsList.push(functionItem);
    }
  };

  hasChanges(): boolean {
    const currentIds = this.selectedFunctionsList.map((item: any) => item.id).sort();
    const initialIds = this.initialSelectedFunctionsList.map(item => item.id).sort();
    return JSON.stringify(currentIds) !== JSON.stringify(initialIds);
  }  

  // remove function out of selected threat to be saved later
  removeFunctionFromThreat(functionItem: IFunctionGoals): void {
    const index = this.selectedFunctionsList.findIndex((item: IFunctionGoals) => item.function_name === functionItem.function_name);
    if (index !== -1) {
      this.selectedFunctionsList.splice(index, 1);
    }
  };

  // save final selected functions to the threat/hazard
  saveSelectedFunctions(): void {
    if (this.currentBuildingId && this.selectedThreatId) {
      if (!this.hasChanges()) {
        console.log('No changes detected. Skip saving.');
        return;
      }
      this.loading = true;
      const payload: any[] = [];
      this.selectedFunctionsList.forEach((functionItem: IFunctionGoals) => {
        payload.push(functionItem.id);
      });
      console.log('Selected Functions List:', payload);
      this.functionService.saveFunctionalAssignment(this.currentBuildingId, String(this.selectedThreatId), payload).subscribe({
        next: (response) => {
          this.loading = false;
          console.log('Functions successfully saved to the threat:', response);
          this.messageModal = 'success';
          this.popupMessage = 'This occurred after updating functional assignment.';
          // Reload the threat functions after saving
          this.loadAllAssignments();
        },
        error: (error) => {
          this.loading = false;
          console.error('Error saving functions to the threat:', error.message);
          this.messageModal = 'unsuccess';
          this.popupMessage = error.message;
        }
      })
    }
  }

  handleOpenModal(modalName: string, selectedItem?: any): void {
    if (modalName === 'function-details') {
      this.showFunctionDetails = true;
    } 
    this.selectedFunction = selectedItem ? selectedItem : null;
  }

  handleCloseModal(modalName: string): void {
    if (modalName === 'function-details') {
      this.showFunctionDetails = false;
    } 
    this.selectedFunction = null;
  }
  
  canSave(): boolean {
    if (!this.currentUser) return false;
    return this.currentUser.can_edit || this.currentUser.can_admin;
  }

  resetMessageModal(): void {
    this.messageModal = null;
    this.popupMessage = '';
  }
}
